import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, CalendarCheck2 } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, EmptyState, DetailDrawer, formatDate } from "@/components/admin/ui";
import { CustomerPickerField } from "./CustomerPickerField";

const labelClass = "text-xs font-bold uppercase tracking-[0.12em] text-slate-400";
const inputClass = "mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white";
const btn = "inline-flex items-center gap-1.5 rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC] disabled:cursor-not-allowed disabled:opacity-50";
const outlineBtn = "inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3.5 py-2 text-xs font-bold text-slate-600 transition-colors duration-150 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10";

const localDateString = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

const NewFollowUpModal = ({ API, meta, onClose, onCreated }) => {
  const [customer, setCustomer] = useState(null);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [dueDate, setDueDate] = useState(localDateString());
  const [assignedUserId, setAssignedUserId] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!customer || !title.trim()) return;
    setSaving(true); setError("");
    try {
      await axios.post(`${API}/org/support/follow-ups`, { customer_id: customer.customer_id, title: title.trim(), notes: notes.trim() || null, due_date: dueDate, assigned_user_id: assignedUserId || null }, { withCredentials: true });
      onCreated();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <DetailDrawer open onClose={onClose} title="New Follow-up" testId="new-followup-drawer">
      <div className="space-y-4" data-testid="new-followup-form">
        {error && <p className="text-sm font-semibold text-red-600 dark:text-red-400" data-testid="new-followup-error">{error}</p>}
        <div><label className={labelClass}>Customer</label><div className="mt-1"><CustomerPickerField API={API} value={customer} onChange={setCustomer} testId="new-followup-customer-picker" /></div></div>
        <div><label className={labelClass}>Title</label><input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="new-followup-title" className={inputClass} /></div>
        <div><label className={labelClass}>Notes</label><textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="new-followup-notes" className={inputClass} /></div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><label className={labelClass}>Due Date</label><input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="new-followup-due-date" className={inputClass} /></div>
          <div>
            <label className={labelClass}>Assignee</label>
            <select value={assignedUserId} onChange={(e) => setAssignedUserId(e.target.value)} data-testid="new-followup-assignee" className={inputClass}>
              <option value="">Unassigned</option>
              {meta.users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="new-followup-cancel" className={outlineBtn}>Cancel</button>
          <button type="button" onClick={submit} disabled={saving || !customer || !title.trim()} data-testid="new-followup-submit" className={btn}>Create Follow-up</button>
        </div>
      </div>
    </DetailDrawer>
  );
};

export const FollowUpsTab = ({ API, meta, canManage, refreshKey }) => {
  const [followUps, setFollowUps] = useState(null);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [status, setStatus] = useState("pending");
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/org/support/follow-ups`, { params: status ? { status } : {}, withCredentials: true }).then(({ data }) => setFollowUps(data.follow_ups)).catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API, status]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const complete = async (id) => {
    setBusyId(id); setActionError("");
    try { await axios.patch(`${API}/org/support/follow-ups/${id}`, { status: "completed" }, { withCredentials: true }); load(); } catch (err) { setActionError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusyId(null); }
  };

  if (error) return <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="support-followups-error">{error}</p>;

  return (
    <>
      <Panel title="Follow-ups" subtitle="Tasks to check back in with a customer." testId="support-followups-tab"
        action={canManage && <button type="button" onClick={() => setCreating(true)} data-testid="support-followups-new-btn" className="flex items-center gap-1.5 rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white hover:bg-[#0040CC]"><Plus size={14} /> New Follow-up</button>}>
        {actionError && <p className="mb-3 text-sm font-semibold text-red-600 dark:text-red-400" data-testid="support-followups-action-error">{actionError}</p>}
        <div className="mb-4 flex gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="support-followups-filter-status" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="">All</option>
          </select>
        </div>
        {!followUps ? <p className="text-sm text-slate-400">Loading…</p> : followUps.length === 0 ? (
          <EmptyState icon={CalendarCheck2} title="No follow-ups." message="Create one to remember to check back in with a customer." testId="support-followups-empty" />
        ) : (
          <div className="space-y-2" data-testid="support-followups-list">
            {followUps.map((f) => (
              <div key={f.follow_up_id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3 dark:border-white/10" data-testid={`support-followup-row-${f.follow_up_id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-slate-900 dark:text-white">{f.title}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{f.customer_name} · Due {formatDate(f.due_date)}{f.assigned_user_name ? ` · ${f.assigned_user_name}` : ""}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {f.is_overdue && <span className="rounded-full border border-red-200 px-2 py-0.5 text-xs font-bold text-red-600 dark:border-red-500/30 dark:text-red-400">Overdue</span>}
                  {f.is_due_today && <span className="rounded-full border border-amber-200 px-2 py-0.5 text-xs font-bold text-amber-600 dark:border-amber-500/30 dark:text-amber-400">Due Today</span>}
                  {f.status === "completed" ? <span className="rounded-full border border-emerald-200 px-2 py-0.5 text-xs font-bold text-emerald-600 dark:border-emerald-500/30 dark:text-emerald-400">Completed</span>
                    : canManage && <button type="button" onClick={() => complete(f.follow_up_id)} disabled={busyId === f.follow_up_id} data-testid={`support-followup-complete-${f.follow_up_id}`} className={outlineBtn}>Mark Done</button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
      {creating && <NewFollowUpModal API={API} meta={meta} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
    </>
  );
};
